'use client';
import React, { useState, useEffect, useRef } from 'react';

const specialties = [
  { value: '', label: 'Select your specialty' },
  { value: 'hematology', label: 'Hematology' },
  { value: 'oncology', label: 'Oncology' },
  { value: 'internal-medicine', label: 'Internal Medicine' },
  { value: 'primary-care', label: 'Primary Care' },
  { value: 'patient', label: 'Patient / Caregiver' },
];

const clinicalInterests = [
  { id: 'coagulation', label: 'Coagulation & Thrombosis' },
  { id: 'red-cell', label: 'Red Cell Disorders' },
  { id: 'myeloid', label: 'Myeloid Neoplasms' },
  { id: 'iron', label: 'Iron Metabolism' },
  { id: 'platelet', label: 'Platelet Disorders' },
];

const bundles: Record<string, { title: string; items: string[] }> = {
  hematology: {
    title: 'Advanced Hematology Bundle',
    items: ['Myeloproliferative Overlap Algorithm', 'HLH HScore Calculator', 'Thrombophilia Timing Protocol', 'NGS Myeloid Panel Interpretation Guide'],
  },
  oncology: {
    title: 'Hematologic Malignancy Bundle',
    items: ['Blast Triage Protocol', 'AML Mutation-Targeted Therapy Matrix', 'MDS IPSS-R Calculator', 'Lymphoma Workup Pathways'],
  },
  'internal-medicine': {
    title: 'Internist\'s Hematology Bundle',
    items: ['Anemia Differential Algorithm', 'Coagulation Panel Interpretation', 'Abnormal Smear Decision Tree', 'Ferritin Elevation Workup'],
  },
  'primary-care': {
    title: 'Primary Care Hematology Bundle',
    items: ['CBC Abnormality Triage Guide', 'When to Refer Checklist', 'Iron Deficiency vs ACD', 'VWD Screening Protocol'],
  },
  patient: {
    title: 'Patient Understanding Bundle',
    items: ['Understanding Your Blood Test Results', 'Bruising & Bleeding Guide', 'Fatigue & Anemia Explained', 'Questions to Ask Your Hematologist'],
  },
};

export default function ResourceSection() {
  const [specialty, setSpecialty] = useState('');
  const [email, setEmail] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const toggleInterest = (id: string) => {
    setInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!specialty || !email) return;
    setSubmitted(true);
  };

  const currentBundle = bundles[specialty];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="resources"
      ref={sectionRef}
      className="py-24 px-6"
      style={{ background: 'linear-gradient(180deg, #EAF2F8 0%, #F4F7FA 100%)' }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="reveal text-center mb-16">
          <span
            className="text-xs font-bold tracking-widest uppercase block mb-4"
            style={{ color: '#89B0D0' }}
          >
            Clinical Resources
          </span>
          <h2
            className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight mb-5"
            style={{ color: '#3B4856' }}
          >
            The Workup Guide
            <br />
            <span className="italic font-light" style={{ color: '#89B0D0' }}>
              Built for Your Practice
            </span>
          </h2>
          <p className="text-lg font-light max-w-xl mx-auto leading-relaxed" style={{ color: '#5A6B7C' }}>
            Tell us your specialty and clinical focus — we'll deliver the exact
            resource bundle your practice needs.
          </p>
        </div>

        <div className="reveal grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Form */}
          <div
            className="rounded-3xl p-8 md:p-10"
            style={{
              background: '#F4F7FA',
              border: '1px solid rgba(137,176,208,0.25)',
              boxShadow: '0 24px 64px rgba(59,72,86,0.08)',
            }}
          >
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="specialty"
                    className="block text-xs font-bold tracking-widest uppercase mb-2"
                    style={{ color: '#8A9AAA' }}
                  >
                    Your Specialty
                  </label>
                  <select
                    id="specialty"
                    value={specialty}
                    onChange={(e) => setSpecialty(e.target.value)}
                    className="form-input form-select w-full px-4 py-3 rounded-xl text-sm font-medium"
                    required
                  >
                    {specialties.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-xs font-bold tracking-widest uppercase mb-2"
                    style={{ color: '#8A9AAA' }}
                  >
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="form-input w-full px-4 py-3 rounded-xl text-sm font-medium"
                    required
                  />
                </div>

                <div>
                  <p
                    className="text-xs font-bold tracking-widest uppercase mb-3"
                    style={{ color: '#8A9AAA' }}
                  >
                    Clinical Interests{' '}
                    <span className="normal-case font-normal" style={{ color: '#C4D9EC' }}>
                      (optional — personalizes your bundle)
                    </span>
                  </p>
                  <div className="space-y-2.5">
                    {clinicalInterests.map((interest) => (
                      <label
                        key={interest.id}
                        className="flex items-center gap-3 cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          className="clinical-checkbox"
                          checked={interests.includes(interest.id)}
                          onChange={() => toggleInterest(interest.id)}
                        />
                        <span
                          className="text-sm font-medium transition-colors"
                          style={{ color: interests.includes(interest.id) ? '#3B4856' : '#5A6B7C' }}
                        >
                          {interest.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-rose w-full py-4 rounded-xl text-base font-semibold flex items-center justify-center gap-2.5"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download My Workup Guide
                </button>

                <p className="text-xs text-center font-light" style={{ color: '#8A9AAA' }}>
                  No spam. Unsubscribe any time. For clinical use only.
                </p>
              </form>
            ) : (
              <div className="text-center py-8">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ background: 'rgba(137,176,208,0.15)', border: '1.5px solid #89B0D0' }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#89B0D0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="font-serif text-2xl mb-3" style={{ color: '#3B4856' }}>
                  Your bundle is on its way.
                </h3>
                <p className="text-sm font-light leading-relaxed" style={{ color: '#5A6B7C' }}>
                  Check <strong>{email}</strong> — your personalized workup guide will arrive within 2 minutes.
                </p>
              </div>
            )}
          </div>

          {/* Bundle Preview */}
          <div className="space-y-6">
            <div>
              <p
                className="text-xs font-bold tracking-widest uppercase mb-4"
                style={{ color: '#89B0D0' }}
              >
                {specialty && currentBundle ? currentBundle.title : 'What you\'ll receive'}
              </p>
              {specialty && currentBundle ? (
                <ul className="space-y-3">
                  {currentBundle.items.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 p-4 rounded-xl"
                      style={{ background: '#F4F7FA', border: '1px solid rgba(137,176,208,0.2)' }}
                    >
                      <span
                        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                        style={{ background: 'rgba(196,91,106,0.1)', color: '#C45B6A' }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-sm font-medium" style={{ color: '#3B4856' }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="space-y-3">
                  {[
                    'Specialty-matched diagnostic algorithms',
                    'Clinical reference cards for common presentations',
                    'Downloadable PDF decision trees',
                    'Personalized based on your interests',
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-4 rounded-xl"
                      style={{
                        background: 'rgba(137,176,208,0.06)',
                        border: '1px solid rgba(137,176,208,0.15)',
                      }}
                    >
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: '#89B0D0' }}
                      />
                      <span className="text-sm font-light" style={{ color: '#5A6B7C' }}>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Testimonial / trust signal */}
            <div
              className="p-6 rounded-2xl"
              style={{
                background: '#3B4856',
                border: '1px solid rgba(137,176,208,0.15)',
              }}
            >
              <p
                className="font-serif text-lg italic leading-relaxed mb-4"
                style={{ color: 'rgba(244,247,250,0.85)' }}
              >
                "The thrombophilia timing guide alone saved me from a workup error
                that would have given three false negatives. This is the resource
                I wish I had in fellowship."
              </p>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: 'rgba(137,176,208,0.2)', color: '#89B0D0' }}
                >
                  DR
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#F4F7FA' }}>
                    Dr. Rachel Okonkwo
                  </p>
                  <p className="text-xs" style={{ color: '#89B0D0' }}>
                    Internal Medicine · Houston, TX
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}